/**
 * Data Preloader
 * Preloads critical data into IndexedDB for offline access
 */

import axios from 'axios';
import offlineManager from './offlineManager';

class DataPreloader {
  constructor() {
    this.isPreloading = false;
    this.preloadComplete = false;
  }

  /**
   * List of critical endpoints to preload
   * Only includes endpoints that work without specific parameters
   */
  getCriticalEndpoints() {
    return [
      // POS Configuration
      { method: 'post', url: 'settings/viewPosConfigurationSettings', data: {} },
      { method: 'get', url: 'inventory/ViewProductVarientSettings' },
      
      // Products and Categories
      { method: 'post', url: 'readymade/viewnewproductlistforothersections', data: {} },
      { method: 'post', url: 'category/view', data: {} },
      { method: 'post', url: 'readymade/categoryfilter', data: {} },
      
      // Customers and Vendors
      { method: 'post', url: 'account/viewCustomerForDropdown', data: {} },
      { method: 'post', url: 'account/viewVendorForDropdown', data: {} },
      { method: 'post', url: 'customer/viewCashCustomer', data: {} },
      
      // Employees
      { method: 'get', url: 'employee/getAllEmployees' },
      
      // Branch and Company Info
      { method: 'post', url: 'admin/viewbranches', data: {} },
      { method: 'post', url: 'admin/viewcompanyinfosforemployee', data: {} },
      
      // Settings
      { method: 'post', url: 'settings/viewCurrency', data: {} },
      { method: 'post', url: 'settings/listcashinorout', data: {} },
      
      // POS Operations
      { method: 'post', url: 'pos/viewFloor', data: {} },
      
      // GL Config
      { method: 'post', url: 'purchase/viewglconfig', data: {} }
      
      // NOTE: Removed endpoints that require specific parameters or don't exist:
      // - employee/viewemployeepermissions (needs employeeId)
      // - pos/empCheckinStatus (needs parameters)
      // - settings/viewactiveshift (404)
      // - settings/posBranchWiseConfig (406 - needs branchId)
      // - settings/fetchpaymentsforposbybranch (needs branchId)
      // - settings/viewcard (404)
      // - pos/viewAggregatorList (404)
      // - pos/listPosOrders (needs parameters)
      // - pos/viewReservationList (404)
      // - pos/listCompletedSelfOrder (needs parameters)
      // - pos/checkInSummaryReport (needs shiftId)
      // - admin/getPosReceiptTemplateModel (404)
    ];
  }

  /**
   * Preload all critical data
   */
  async preloadAllData() {
    if (this.isPreloading || this.preloadComplete) {
      console.log('⚠️ Preload already in progress or complete');
      return;
    }

    if (!navigator.onLine) {
      console.log('📴 Offline: Skipping preload');
      return;
    }

    try {
      this.isPreloading = true;
      console.log('🔄 Starting data preload...');

      const endpoints = this.getCriticalEndpoints();
      let successCount = 0;
      let failCount = 0;

      for (const endpoint of endpoints) {
        try {
          const response = await axios({
            method: endpoint.method,
            url: endpoint.url,
            data: endpoint.data || undefined
          });

          // Data will be automatically cached by the response interceptor
          successCount++;
          
          // Small delay to avoid overwhelming the server
          await this.delay(50);
          
        } catch (error) {
          failCount++;
          // Only log if it's a real error (not 404/400 which are expected)
          if (error.response?.status !== 404 && error.response?.status !== 400) {
            console.error(`❌ Preload failed ${endpoint.url}:`, error.message);
          }
        }
      }

      console.log(`✅ Data preload complete: ${successCount}/${endpoints.length} endpoints cached`);
      
      // Preload images after data
      await this.preloadImages();
      
      this.preloadComplete = true;

    } catch (error) {
      console.error('Error during preload:', error);
    } finally {
      this.isPreloading = false;
    }
  }

  /**
   * Preload product images and company logo for offline access
   */
  async preloadImages() {
    try {
      console.log('🖼️ Preloading ALL images...');
      
      // Get company logo from localStorage or Redux
      const companyLogo = localStorage.getItem('companyLogo');
      if (companyLogo) {
        await this.cacheImage(companyLogo);
        console.log('✅ Company logo cached');
      }
      
      // Get product images from cached products
      const productsResponse = await axios.post('readymade/viewnewproductlistforothersections', {});
      const products = productsResponse.data?.data || [];
      
      console.log(`📦 Found ${products.length} products to cache images for`);
      
      let cachedImages = 0;
      let failedImages = 0;
      
      // Preload ALL product images (not just 50)
      for (const product of products) {
        if (product.imageUrl) {
          try {
            await this.cacheImage(product.imageUrl);
            cachedImages++;
            
            // Log progress every 50 images
            if (cachedImages % 50 === 0) {
              console.log(`📸 Cached ${cachedImages}/${products.length} images...`);
            }
            
            // Small delay to avoid overwhelming the network
            await this.delay(50);
          } catch (error) {
            failedImages++;
            // Silently fail for individual images
          }
        }
      }
      
      console.log(`✅ Preloaded ${cachedImages} product images (${failedImages} failed)`);
      
    } catch (error) {
      console.warn('⚠️ Image preload failed:', error.message);
    }
  }

  /**
   * Cache a single image using fetch (will be cached by service worker)
   */
  async cacheImage(imageUrl) {
    try {
      const response = await fetch(imageUrl, { mode: 'no-cors' });
      return response;
    } catch (error) {
      // Silently fail
      return null;
    }
  }

  /**
   * Delay utility
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Reset preload status (for manual re-preload)
   */
  reset() {
    this.preloadComplete = false;
  }
}

// Export singleton instance
const dataPreloader = new DataPreloader();
export default dataPreloader;
